import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { clearAuth, getAuthToken } from "../auth/auth";
import SiteLayout from "../components/SiteLayout";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://pelaj-forever2025-production.up.railway.app/weddingAccess";

function extractQrCode(raw) {
  if (!raw) return "";
  const trimmed = raw.trim();

  try {
    const u = new URL(trimmed);
    const parts = u.pathname.split("/").filter(Boolean);
    return parts[parts.length - 1] || "";
  } catch {
    const parts = trimmed.split("/").filter(Boolean);
    return parts[parts.length - 1] || "";
  }
}

export default function AdminCheckIn() {
  const navigate = useNavigate();
  const token = useMemo(() => getAuthToken(), []);

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [isScanning, setIsScanning] = useState(false);
  const [scanStatus, setScanStatus] = useState("Scanner idle");

  const qrInstanceRef = useRef(null);
  const isStartingRef = useRef(false);

  const logout = () => {
    clearAuth();
    navigate("/admin/login", { replace: true });
  };

  const handleBack = async () => {
    setError("");
    setResult(null);
    setLoading(false);
    setScanStatus("Scanner idle");

    await stopScanner();
  };

  const checkInWithValue = async (value) => {
    setError("");
    setResult(null);

    const code = extractQrCode(value);
    if (!code) {
      setError("Invalid QR value. Try scanning again.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/check-in/${code}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const json = await res.json();

      if (!res.ok) {
        throw new Error(json?.message || json?.error || "Check-in failed");
      }

      setResult(json);
    } catch (err) {
      setError(err.message || "Unable to check in guest");
    } finally {
      setLoading(false);
    }
  };

  const startScanner = async () => {
    if (isScanning || isStartingRef.current) return;
    isStartingRef.current = true;

    setError("");
    setResult(null);
    setScanStatus("Starting camera...");

    try {
      const el = document.getElementById("qr-reader");
      if (el) el.innerHTML = "";

      const html5QrCode = new Html5Qrcode("qr-reader");
      qrInstanceRef.current = html5QrCode;

      const config = {
        fps: 10,
        qrbox: (viewfinderWidth, viewfinderHeight) => {
          const minEdge = Math.min(viewfinderWidth, viewfinderHeight);
          const size = Math.floor(minEdge * 0.72);
          return { width: size, height: size };
        },

        aspectRatio: 1.0,
        disableFlip: true,
      };

      const onSuccess = async (decodedText) => {
        setScanStatus("QR detected. Checking in...");

        await stopScanner();

        await checkInWithValue(decodedText);
      };

      const onError = () => {
        // Ignore per-frame decode failures
      };

      const cameraConfig = { facingMode: "environment" };

      await html5QrCode.start(cameraConfig, config, onSuccess, onError);
      setIsScanning(true);
      setScanStatus("Scanning... Point camera at the QR code");
    } catch (e) {
      setScanStatus("Failed to start scanner. Allow camera permission and try again.");
      setError(
        e?.message ||
          "Failed to start scanner. If you are not on localhost, you must use HTTPS."
      );

      try {
        if (qrInstanceRef.current) {
          await qrInstanceRef.current.stop();
          await qrInstanceRef.current.clear();
        }
      } catch {}
      qrInstanceRef.current = null;
      setIsScanning(false);
    } finally {
      isStartingRef.current = false;
    }
  };

  const stopScanner = async () => {
    if (!qrInstanceRef.current) {
      setIsScanning(false);
      setScanStatus("Scanner idle");
      return;
    }

    try {
      if (qrInstanceRef.current.isScanning) {
        await qrInstanceRef.current.stop();
      }
      await qrInstanceRef.current.clear();
    } catch {
      // no-op
    } finally {
      qrInstanceRef.current = null;
      setIsScanning(false);
      setScanStatus("Scanner stopped");
    }
  };

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  useEffect(() => {
    if (!token) {
      navigate("/admin/login", { replace: true });
    }
  }, [token, navigate]);

  return (
    <SiteLayout>
        <div className="admin-page">
            <div className="admin-card">
                <div className="admin-row">
                <h2 className="admin-title">Check In Guest</h2>

                <div style={{ display: "flex", gap: 10 }}>
                    <button className="admin-btn outline" onClick={handleBack}>
                    Back
                    </button>

                    <button className="admin-btn outline" onClick={logout}>
                    Logout
                    </button>
                </div>
                </div>

                <p className="admin-subtitle">
                Scan the QR code with your back camera.
                </p>

                {error && <div className="admin-error">{error}</div>}
                <div className="scanner-panel">
                <div className="scanner-row">
                    <div className="scanner-status">{scanStatus}</div>
                </div>

                <div className="qr-reader-box">
                    <div id="qr-reader" className="qr-reader-inner" />
                </div>

                <div className="scanner-actions">
                    {!isScanning ? (
                    <button
                        className="admin-btn primary"
                        onClick={startScanner}
                        disabled={loading}
                    >
                        {loading ? "Checking in..." : "Start Camera Scan"}
                    </button>
                    ) : (
                    <button className="admin-btn outline" onClick={stopScanner}>
                        Stop Camera
                    </button>
                    )}
                </div>
                </div>

                {result && (
                <div className="admin-result">
                    <div className={`pill ${result.status === "SUCCESS" ? "ok" : "bad"}`}>
                    {result.status}
                    </div>

                    <div className="admin-result-grid">
                    <div>
                        <div className="k">Guest Name</div>
                        <div className="v">{result.guestName || "-"}</div>
                    </div>

                    <div>
                        <div className="k">Has Checked In</div>
                        <div className="v">{String(result.hasCheckedIn)}</div>
                    </div>

                    <div>
                        <div className="k">Party Band Code</div>
                        <div className="v">{result.partyBandCode || "-"}</div>
                    </div>
                    </div>
                </div>
                )}
            </div>
        </div>
    </SiteLayout>
  );
}





// import React, { useEffect, useMemo, useRef, useState } from "react";
// import { useNavigate } from "react-router-dom";
// import { Html5Qrcode } from "html5-qrcode";
// import { clearAuth, getAuthToken } from "../auth/auth";

// const API_BASE_URL =
//   process.env.REACT_APP_API_BASE_URL || "https://pelaj-forever2025-production.up.railway.app/weddingAccess";

// function extractQrCode(raw) {
//   if (!raw) return "";
//   const trimmed = raw.trim();

//   try {
//     // If it is a URL, get the last path segment
//     const u = new URL(trimmed);
//     const parts = u.pathname.split("/").filter(Boolean);
//     return parts[parts.length - 1] || "";
//   } catch {
//     // Not a URL, treat as plain string and get last segment if slashes exist
//     const parts = trimmed.split("/").filter(Boolean);
//     return parts[parts.length - 1] || "";
//   }
// }

// export default function AdminCheckIn() {
//   const navigate = useNavigate();
//   const token = useMemo(() => getAuthToken(), []);

//   const [rawInput, setRawInput] = useState("");
//   const [result, setResult] = useState(null);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState("");

//   // Camera/scanner state
//   const [cameras, setCameras] = useState([]);
//   const [selectedCameraId, setSelectedCameraId] = useState("");
//   const [isScanning, setIsScanning] = useState(false);
//   const [scanStatus, setScanStatus] = useState("Scanner idle");

//   const qrInstanceRef = useRef(null);
//   const isStartingRef = useRef(false);

//   const logout = () => {
//     clearAuth();
//     navigate("/admin/login", { replace: true });
//   };

//   const checkInWithValue = async (value) => {
//     setError("");
//     setResult(null);

//     const code = extractQrCode(value);
//     if (!code) {
//       setError("Invalid QR value. Try scanning again or paste a valid link/code.");
//       return;
//     }

//     setLoading(true);
//     try {
//       const res = await fetch(`${API_BASE_URL}/admin/check-in/${code}`, {
//         method: "GET",
//         headers: {
//           Authorization: `Bearer ${token}`,
//         },
//       });

//       const json = await res.json();

//       if (!res.ok) {
//         throw new Error(json?.message || json?.error || "Check-in failed");
//       }

//       setResult(json);
//     } catch (err) {
//       setError(err.message || "Unable to check in guest");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const checkInFromInput = async () => {
//     await checkInWithValue(rawInput);
//   };

//   // Load available cameras
//   useEffect(() => {
//     let mounted = true;

//     Html5Qrcode.getCameras()
//       .then((devices) => {
//         if (!mounted) return;
//         setCameras(devices || []);
//         // Prefer back camera on phones if possible
//         if (devices && devices.length > 0) {
//           setSelectedCameraId(devices[0].id);
//         }
//       })
//       .catch(() => {
//         // This can fail if browser blocks camera access or not HTTPS (except localhost)
//         setScanStatus("Camera list unavailable. Make sure camera permission is allowed.");
//       });

//     return () => {
//       mounted = false;
//     };
//   }, []);

//   const startScanner = async () => {
//     if (isScanning || isStartingRef.current) return;
//     isStartingRef.current = true;

//     setError("");
//     setScanStatus("Starting camera...");

//     try {
//       // Create instance bound to the div id
//       const html5QrCode = new Html5Qrcode("qr-reader");
//       qrInstanceRef.current = html5QrCode;

//       const config = {
//         fps: 10,
//         qrbox: { width: 280, height: 280 },
//         aspectRatio: 1.0,
//       };

//       const onSuccess = async (decodedText) => {
//         // Stop scanning as soon as we get one successful read
//         setScanStatus("QR detected. Checking in...");
//         setRawInput(decodedText);

//         await stopScanner();
//         await checkInWithValue(decodedText);
//       };

//       const onError = () => {
//         // Ignore per-frame decode failures
//       };

//       // Start with selected camera if available, else default to environment camera
//       const cameraConfig = selectedCameraId
//         ? { deviceId: { exact: selectedCameraId } }
//         : { facingMode: "environment" };

//       await html5QrCode.start(cameraConfig, config, onSuccess, onError);
//       setIsScanning(true);
//       setScanStatus("Scanning... Point camera at the QR code");
//     } catch (e) {
//       setScanStatus("Failed to start scanner. Allow camera permission and try again.");
//       setError(
//         e?.message ||
//           "Failed to start scanner. If you are not on localhost, you must use HTTPS."
//       );
//       // cleanup if partially created
//       try {
//         if (qrInstanceRef.current) {
//           await qrInstanceRef.current.stop();
//           await qrInstanceRef.current.clear();
//         }
//       } catch {}
//       qrInstanceRef.current = null;
//       setIsScanning(false);
//     } finally {
//       isStartingRef.current = false;
//     }
//   };

//   const stopScanner = async () => {
//     if (!qrInstanceRef.current) {
//       setIsScanning(false);
//       setScanStatus("Scanner idle");
//       return;
//     }

//     try {
//       if (qrInstanceRef.current.isScanning) {
//         await qrInstanceRef.current.stop();
//       }
//       await qrInstanceRef.current.clear();
//     } catch {
//       // no-op
//     } finally {
//       qrInstanceRef.current = null;
//       setIsScanning(false);
//       setScanStatus("Scanner stopped");
//     }
//   };

//   // Cleanup when leaving page
//   useEffect(() => {
//     return () => {
//       stopScanner();
//     };
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   return (
//     <div className="admin-page">
//       <div className="admin-card">
//         <div className="admin-row">
//           <h2 className="admin-title">Check In Guest</h2>
//           <button className="admin-btn outline" onClick={logout}>
//             Logout
//           </button>
//         </div>

//         <p className="admin-subtitle">
//           Scan the QR code with your camera or paste the invitation link/code.
//         </p>

//         {error && <div className="admin-error">{error}</div>}

//         {/* Camera Scanner UI */}
//         <div className="scanner-panel">
//           <div className="scanner-row">
//             <div className="scanner-status">{scanStatus}</div>

//             {cameras.length > 0 && (
//               <select
//                 className="admin-input"
//                 value={selectedCameraId}
//                 onChange={(e) => setSelectedCameraId(e.target.value)}
//                 disabled={isScanning}
//                 style={{ height: 40 }}
//               >
//                 {cameras.map((c) => (
//                   <option key={c.id} value={c.id}>
//                     {c.label || `Camera ${c.id}`}
//                   </option>
//                 ))}
//               </select>
//             )}
//           </div>

//           <div id="qr-reader" className="qr-reader-box" />

//           <div className="scanner-actions">
//             {!isScanning ? (
//               <button className="admin-btn primary" onClick={startScanner}>
//                 Start Camera Scan
//               </button>
//             ) : (
//               <button className="admin-btn outline" onClick={stopScanner}>
//                 Stop Camera
//               </button>
//             )}
//           </div>
//         </div>

//         {/* Manual paste fallback */}
//         <div className="admin-form" style={{ marginTop: 16 }}>
//           <label className="admin-label">
//             QR Value / Invitation Link
//             <input
//               className="admin-input"
//               value={rawInput}
//               onChange={(e) => setRawInput(e.target.value)}
//               placeholder="Paste UUID or https://.../invitation/{qrCode}"
//             />
//           </label>

//           <button
//             className="admin-btn primary"
//             onClick={checkInFromInput}
//             disabled={loading}
//           >
//             {loading ? "Checking in..." : "Check In"}
//           </button>
//         </div>

//         {/* Result */}
//         {result && (
//           <div className="admin-result">
//             <div className={`pill ${result.status === "SUCCESS" ? "ok" : "bad"}`}>
//               {result.status}
//             </div>

//             <div className="admin-result-grid">
//               <div>
//                 <div className="k">Guest Name</div>
//                 <div className="v">{result.guestName || "-"}</div>
//               </div>

//               <div>
//                 <div className="k">Has Checked In</div>
//                 <div className="v">{String(result.hasCheckedIn)}</div>
//               </div>

//               <div>
//                 <div className="k">Party Band Code</div>
//                 <div className="v">{result.partyBandCode || "-"}</div>
//               </div>
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// }
